import { createFileRoute, Link } from "@tanstack/react-router";
import { BUSINESS, SERVICES, CITIES } from "@/lib/site-data";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: `${BUSINESS.shortName} — SEO & Google Ads for Trades Contractors` },
      {
        name: "description",
        content: `SEO, Google Ads, and websites for HVAC, plumbing, electrical, and home-service contractors in Lehi and across Utah County. Lead-gen built for the trades.`,
      },
      { property: "og:title", content: `${BUSINESS.shortName} — Trades SEO in Lehi, UT` },
      {
        property: "og:description",
        content: "Local SEO, Google Ads, and conversion-built websites for Utah trades contractors.",
      },
      { property: "og:url", content: "/" },
    ],
    links: [{ rel: "canonical", href: "https://lehitradessaeo.com/" }],
  }),
  component: Index,
});

function Index() {
  return (
    <main>
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-6xl items-center gap-16 px-6 py-24 md:grid-cols-2 md:py-32">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Lehi, UT · SEO for the Trades
            </p>
            <h1 className="mt-6 font-display text-5xl leading-[1.05] tracking-tight md:text-7xl">
              More booked jobs.<br />
              <em className="text-primary not-italic md:italic">Less wasted ad spend.</em>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              {BUSINESS.tagline} We help HVAC, plumbing, electrical, roofing,
              and remodeling contractors rank in the Map Pack and run Google
              Ads that pay for themselves.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
              >
                Get a free SEO audit
              </Link>
              <Link
                to="/services"
                className="rounded-full px-6 py-3 text-sm font-medium text-foreground transition hover:bg-secondary"
              >
                See services →
              </Link>
            </div>
          </div>
          <aside className="rounded-2xl border border-border bg-card p-8 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Why trades contractors hire us
            </p>
            <ul className="mt-6 space-y-4 text-sm leading-relaxed">
              <li className="flex gap-3"><span className="font-display text-lg text-primary">01</span> Trades-only — no competing clients in your city</li>
              <li className="flex gap-3"><span className="font-display text-lg text-primary">02</span> Cost-per-lead reporting, not vanity traffic</li>
              <li className="flex gap-3"><span className="font-display text-lg text-primary">03</span> Local SEO + Google Ads + LSAs under one roof</li>
              <li className="flex gap-3"><span className="font-display text-lg text-primary">04</span> Month-to-month after the first 90 days</li>
              <li className="flex gap-3"><span className="font-display text-lg text-primary">05</span> Based in Lehi — we know the Utah market</li>
            </ul>
          </aside>
        </div>
      </section>

      <section className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Results, not impressions
            </p>
            <h2 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
              Real numbers. Real booked jobs.
            </h2>
            <p className="mt-5 text-lg text-muted-foreground">
              We don't report on vanity metrics like "impressions." We measure
              success by your cost-per-lead and how often your phone rings.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <article className="rounded-2xl border border-border bg-card p-7">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                Map Pack dominance
              </p>
              <p className="mt-5 font-display text-4xl tracking-tight md:text-5xl">
                +140%
              </p>
              <p className="mt-1 text-sm font-medium text-foreground">
                click-to-call volume
              </p>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                Moved a Utah County HVAC contractor from page 3 of Google to the
                top 3 of the Local Map Pack in under 90 days.
              </p>
              <p className="mt-4 border-t border-border pt-4 text-sm leading-relaxed text-foreground">
                Result: consistent, daily emergency service calls without paying
                for clicks.
              </p>
            </article>

            <article className="rounded-2xl border border-border bg-card p-7">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                Lead-gen efficiency
              </p>
              <p className="mt-5 font-display text-4xl tracking-tight md:text-5xl">
                $85 → $28
              </p>
              <p className="mt-1 text-sm font-medium text-foreground">
                cost-per-lead (CPL)
              </p>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                Audited a bloated Google Ads account for a local plumber, cut
                wasted spend, and built tightly targeted emergency-call
                campaigns.
              </p>
              <p className="mt-4 border-t border-border pt-4 text-sm leading-relaxed text-foreground">
                Result: tripled lead volume on the exact same monthly budget.
              </p>
            </article>

            <article className="rounded-2xl border border-border bg-card p-7">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                Territory expansion
              </p>
              <p className="mt-5 font-display text-4xl tracking-tight md:text-5xl">
                3×
              </p>
              <p className="mt-1 text-sm font-medium text-foreground">
                organic service calls in 6 months
              </p>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                Deployed localized, trade-specific service-area pages across
                Silicon Slopes and south Salt Lake County.
              </p>
              <p className="mt-4 border-t border-border pt-4 text-sm leading-relaxed text-foreground">
                Result: captured high-intent, long-tail searches in fast-growing
                neighborhoods before competitors noticed.
              </p>
            </article>
          </div>

          <p className="mt-8 text-xs text-muted-foreground">
            Representative results from trades clients. Outcomes vary by market,
            budget, and starting position.{" "}
            <Link to="/case-studies" className="font-medium text-primary hover:underline">
              Read the full case studies →
            </Link>
          </p>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Built one trade at a time
          </p>
          <h2 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
            Marketing hubs by trade
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Deep, interconnected playbooks for the trades we serve. Each hub
            links every silo — local SEO, paid, GBP, and GEO — for one trade.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/hubs/$trade" params={{ trade: "plumber" }} className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-primary/40 hover:text-primary">Plumbers</Link>
            <Link to="/hubs/$trade" params={{ trade: "electrician" }} className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-primary/40 hover:text-primary">Electricians</Link>
            <Link to="/hubs/$trade" params={{ trade: "hvac" }} className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-primary/40 hover:text-primary">HVAC</Link>
            <Link to="/hubs/$trade" params={{ trade: "roofer" }} className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-primary/40 hover:text-primary">Roofers</Link>
            <Link to="/hubs" className="rounded-full px-4 py-2 text-sm font-medium text-primary hover:underline">All hubs →</Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-24">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              What we do
            </p>
            <h2 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
              Lead-gen services for trades contractors
            </h2>
          </div>
          <Link to="/services" className="hidden text-sm font-medium text-primary hover:underline md:block">
            All services →
          </Link>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to="/services/$service"
              params={{ service: s.slug }}
              className="group flex items-start justify-between rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40 hover:shadow-sm"
            >
              <div>
                <div className="font-display text-2xl tracking-tight">{s.name}</div>
                <p className="mt-2 text-sm text-muted-foreground">{s.tagline}</p>
              </div>
              <span className="ml-4 text-primary transition group-hover:translate-x-1">→</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Where our clients win
          </p>
          <h2 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
            Ranking trades contractors across Utah County
          </h2>
          <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {CITIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to="/service-areas/$city"
                  params={{ city: c.slug }}
                  className="block rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium transition hover:border-primary/40 hover:text-primary"
                >
                  {c.name}, {c.region}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-28 text-center">
        <h2 className="font-display text-4xl tracking-tight md:text-6xl">
          Ready to outrank your competition?
        </h2>
        <p className="mt-5 text-lg text-muted-foreground">
          Free 30-minute audit of your site, GBP, and current ad spend — no pitch.
        </p>
        <Link
          to="/contact"
          className="mt-8 inline-block rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
        >
          Book a free audit
        </Link>
      </section>
    </main>
  );
}
